import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatTableModule } from '@angular/material/table';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatOption } from '@angular/material/core';
import { WorkspaceRole, WorkspaceUser } from '@workspaces/models/users';
import { debounceTime, finalize, map, Observable, of, startWith, Subscription, take, tap } from 'rxjs';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/state/app.state';
import { userManagementFeature, UserManagementFetch } from '@workspaces/store/user-management.feature';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { WorkspaceUserService } from '@workspaces/services/user.service';
import { UserDelete, userDeleteFeature } from '@workspaces/store/user-delete.feature';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationModalComponent } from '../../../confirmation-modal/confirmation-modal.component';
import { UserRoleChange, userRoleChangeFeature } from '@workspaces/store/user-role-change.feature';
import { RequiredRoleDirective } from '@workspaces/utils/workspace-guard.directive';
import { StatusChipComponent } from '../status-chip/status-chip.component';
import { EditComponent } from '@workspaces/utils/edit-component';
import { WorkspaceFetch, workspaceListFeature } from '@workspaces/store/workspace-list.feature';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss', '../../global-workspace-styles.scss'],
  standalone: true,
  imports: [
    MatIcon,
    MatError,
    MatTableModule,
    MatFormField,
    MatLabel,
    MatOption,
    ReactiveFormsModule,
    CommonModule,
    MatTooltipModule,
    MatButton,
    MatProgressSpinner,
    MatInput,
    MatSelect,
    MatIconButton,
    RequiredRoleDirective,
    StatusChipComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserManagementComponent extends EditComponent implements OnInit, OnDestroy {
  private readonly sub = new Subscription();
  public displayedColumns = ['role_icon', 'email', 'role', 'status', 'actions'] as const;
  public roleOptions: WorkspaceRole[] = ['admin', 'editor', 'viewer', 'reviewer'];

  private uniqueEmailValidator: AsyncValidatorFn = (control: AbstractControl): Observable<ValidationErrors | null> => {
    if (!this.users$ || typeof control.value !== 'string') {
      return of(null);
    }

    const emailToCheck = control.value?.toLowerCase().trim();
    return this.users$.pipe(
      startWith([]),
      debounceTime(300),
      map(users => users.map(user => user.email.toLowerCase())),
      map(existingEmails => {
        if (existingEmails.includes(emailToCheck)) {
          return { emailExists: true };
        }
        return null;
      }),
      take(1)
    );
  };

  public readonly inviteFormControls = {
    email: new FormControl(
      '',
      [Validators.email, Validators.required],
      [control => this.uniqueEmailValidator(control)]
    ),
    role: new FormControl('', [Validators.required])
  };
  public inviteFormGroup = new FormGroup(this.inviteFormControls);
  public users$!: Observable<WorkspaceUser[]>;
  public userManagementIsLoading$!: Observable<boolean>;
  public deleteUsers: Record<string, boolean> = {};
  public changeRoleUsers: Record<string, boolean> = {};
  public isDisabledEdit$!: Observable<boolean>;

  constructor(
    private readonly store: Store<AppState>,
    protected override readonly route: ActivatedRoute,
    protected override readonly snackbar: MatSnackBar,
    protected override readonly router: Router,
    private readonly workspaceUserService: WorkspaceUserService,
    private readonly dialog: MatDialog
  ) {
    super(route, snackbar, router);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.store.dispatch(UserManagementFetch({ workspaceId: this.workspaceId }));
    this.store.dispatch(WorkspaceFetch({ workspaceId: this.workspaceId }));

    this.isDisabledEdit$ = this.store.select(workspaceListFeature.workspaceById(this.workspaceId)).pipe(
      map(workspace => !!workspace?.is_archived || !!workspace?.is_deleted),
      startWith(false),
      tap(isDisabledEdit => {
        if (isDisabledEdit) {
          this.inviteFormGroup.disable();
        } else {
          this.inviteFormGroup.enable();
        }
      })
    );
    this.users$ = this.store.select(userManagementFeature.selectData);
    this.userManagementIsLoading$ = this.store.select(userManagementFeature.userManagementIsLoading);

    this.sub.add(
      this.store.select(userDeleteFeature.userDeletesAreBeingDeleted).subscribe(deleteUsers => {
        this.deleteUsers = deleteUsers;
      })
    );
    this.sub.add(
      this.store.select(userRoleChangeFeature.userRoleChangesAreBeingChanged).subscribe(changeRoleUsers => {
        this.changeRoleUsers = changeRoleUsers;
      })
    );
  }
  onDeleteUser(user: WorkspaceUser): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      maxWidth: '400px',
      data: {
        message: `Do you want to delete ${user.email} user from the workspace?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(UserDelete({ userId: user.id, workspaceId: this.workspaceId }));
      }
    });
  }

  onChangeRole(user: WorkspaceUser, newRole: WorkspaceRole): void {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      maxWidth: '400px',
      data: {
        message: `Do you want to change ${user.email} role to ${newRole}?`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(UserRoleChange({ userId: user.id, workspaceId: this.workspaceId, role: newRole }));
      }
    });
  }

  onSubmitInvite(): void {
    if (this.inviteFormGroup.valid) {
      const { email, role } = this.inviteFormGroup.value;
      this.inviteFormGroup.disable();
      if (!this.roleOptions.includes(role as WorkspaceRole)) {
        throw Error('Missing or invalid role');
      }
      if (typeof email !== 'string') {
        throw Error('Missing or invalid email');
      }
      this.sub.add(
        //Single usecase, dont have to cache. Ngrx feature not required
        this.workspaceUserService
          .inviteUser(this.workspaceId, { email, role: role as WorkspaceRole }) // Can cast as we checked before
          .pipe(
            finalize(() => {
              this.inviteFormGroup.enable();
              this.inviteFormGroup.reset();
              this.store.dispatch(UserManagementFetch({ workspaceId: this.workspaceId }));
            })
          )
          .subscribe()
      );
    } else {
      this.inviteFormGroup.markAllAsTouched();
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }
}
